import { useState, useEffect, useCallback, useRef } from 'react'
import { fetchData, BOOKABLE_CATEGORIES } from '../../../reusable'
import moment from 'moment'

// ─── Types ───
interface FilterParams {
  categoryId?: string | number
  fromDate?: string
  toDate?: string
}

interface StatusCount {
  count: number
  percent: number
}

export interface BookingSummary {
  totalBookings: number
  confirmed: StatusCount
  cancelled: StatusCount
  completed: StatusCount
  pending: StatusCount
  rejected: StatusCount
  repeatCustomers: StatusCount
  totalCustomers: number
  [key: string]: number | StatusCount // allow dynamic keys
}

interface TrendItem {
  month: string
  label: string
  confirmed: number
  cancelled: number
}

interface ListingItem {
  categoryId: number
  actionId: number
  listingName: string
  totalBookings: number
  cancelled: number
}

interface CancellationReason {
  reason: string
  count: number
  percent: number
}

export interface TopCustomer {
  userId: number
  fullName: string
  email: string
  totalBookings: number
  totalSpent: number
  lastBooking: string
}

interface SelectOption {
  value: string
  label: string
}

const DEFAULT_SUMMARY: BookingSummary = {
  totalBookings: 0,
  confirmed: { count: 0, percent: 0 },
  cancelled: { count: 0, percent: 0 },
  completed: { count: 0, percent: 0 },
  pending: { count: 0, percent: 0 },
  rejected: { count: 0, percent: 0 },
  repeatCustomers: { count: 0, percent: 0 },
  totalCustomers: 0,
}

// ─── Helpers ───
const buildParams = (category: SelectOption | null, dateRange: Date[]): FilterParams => {
  const params: FilterParams = {}
  if (category?.value) params.categoryId = category.value
  if (dateRange?.length === 2) {
    params.fromDate = moment(dateRange[0]).format('DD-MM-YYYY')
    params.toDate = moment(dateRange[1]).format('DD-MM-YYYY')
  }
  return params
}

const safeApiCall = async (endpoint: string, params: Record<string, any>): Promise<any> => {
  try {
    const res = await fetchData(endpoint, params)
    if (res?.status == '200' && res?.data) return res.data
    return null
  } catch {
    return null
  }
}

// ─── Hooks ───
export const useBookingAnalytics = (category: SelectOption | null, dateRange: Date[]) => {
  const [summary, setSummary] = useState<BookingSummary>(DEFAULT_SUMMARY)
  const [trend, setTrend] = useState<TrendItem[]>([])
  const [byListing, setByListing] = useState<ListingItem[]>([])
  const [cancellationReasons, setCancellationReasons] = useState<CancellationReason[]>([])
  const [topCustomers, setTopCustomers] = useState<TopCustomer[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const isMounted = useRef(true)

  useEffect(() => {
    isMounted.current = true
    return () => { isMounted.current = false }
  }, [])

  const fetchAll = useCallback(async () => {
    setIsLoading(true)
    const params = buildParams(category, dateRange)

    const [summaryData, trendData, listingData, reasonsData, customersData] = await Promise.all([
      safeApiCall('admin/bookinganalyticssummary', params),
      safeApiCall('admin/bookingtrend', params),
      safeApiCall('admin/bookingsbylisting', params),
      safeApiCall('admin/cancellationreasonbreakdown', params),
      safeApiCall('admin/topcustomers', params),
    ])

    if (!isMounted.current) return

    setSummary(summaryData ?? DEFAULT_SUMMARY)
    setTrend(Array.isArray(trendData) ? trendData : [])
    setByListing(Array.isArray(listingData) ? listingData : [])
    setCancellationReasons(Array.isArray(reasonsData?.breakdown) ? reasonsData.breakdown : [])
    setTopCustomers(Array.isArray(customersData) ? customersData : [])
    setIsLoading(false)
  }, [category, dateRange])

  useEffect(() => {
    fetchAll()
  }, [fetchAll])

  return { summary, trend, byListing, cancellationReasons, topCustomers, isLoading }
}

export const ALL_CATEGORY_OPTION: SelectOption = { value: '', label: 'All' }

export const useCategoryList = (): SelectOption[] => {
  const [categoryList] = useState<SelectOption[]>([ALL_CATEGORY_OPTION, ...BOOKABLE_CATEGORIES])
  return categoryList
}
